<template lang="pug">
	.station-map-selector.flex-col.space-large
		CartoMapComponent.stations-map(ref="map",
			:layers="layers",
			:geoJsonStyle="{ fillColor: '#f27649', fillOpacity: 0.1, color: '#f27649', colorOpacity: 0.3, weight: 1 }",
			:showFilters="true",
			:filters="disabled ? [] : ['circle']",
			:searchPlaceEnabled="false",
			:circleDrawings="circleDrawings",
			:class="{ empty: !stations.length }",
			:actionsVisible="!disabled",
			:mapPosition="mapPosition",
			@filter-changed="onFilterChanged"
		)
			.flex-row.space(slot="search-actions")
				Dropdown.selector.flex-1(
					ref="stationsDD",
					:getTotal="r => r.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="(a,b,c) => $Data.getExternalStations(a,b,{...c, allCountries: false, allStations: false, onlyMyStations })",
					:columns="dropdownColumns",
					displayExpr="Name",
					valueExpr="Oid",
					v-model="searchStations",
					:placeholder="$tCaption('Search stations or use circle tool')",
					:showColumnHeaders="true",
					:filtersVisible="false",
					:searchVisible="true",
					:multiple="true",
					:minSearchLength="0",
					:width="300",
					@select-data="onSelectData",
					@opened="onStationsSearchOpenedChange")
					template(v-slot:nameTemplate="{ data }")
						.flex-row.space.station-map-selector.align-center
							img.dropdown-image(v-if="data.HasImage", :src="getStationImageUrl(data.Oid)")
							span {{data.Name}}
		.flex-col.space(v-if="isPolygonSearchInitialized")
			.flex-row.space.align-center
				.font-big.upper {{$tCaption('Search results')}} ({{polygonSearchStations.length}})
				DxButton(:hint="$tCaption('Clear results')", icon="close", stylingMode="text", @click="isPolygonSearchInitialized = false; polygonSearchStations = []; clearFilters()")
			.flex-row.wrap
				Loader(v-if="isPolygonSearching", :text="$tCaption('Searching stations')")
				template(v-else)
					span(v-if="!filteredPolygonSearchStations.length") {{$tCaption('No data', '@actions')}}
					.station.search-result.flex-col.space.align-center.center.clickable(v-else, v-for="station in filteredPolygonSearchStations", :key="station.Oid"
						@click="highlightedStation = station", :class="{ highlighted: highlightedStation === station }", :style="{ '--bg': station.HasImage ? `url('${getStationImageUrl(station.Oid)}')` : '#fff' }")
						.flex-col.space.align-center.center.station-info.flex-1
							img(v-if="station.HasImage", :src="getStationImageUrl(station.Oid)")
							.font-big {{`${station.Name}${station.Country ? ` (${station.Country.Code})` : ''}`}}
							.format-names.flex-row.text-center
								.flex-col
									span(v-if="station.FormatNames", v-for="format in getFormatNames(station)") {{format}}
							.slogan(v-if="station.Slogan") {{station.Slogan}}							
						.add-button.flex-row.align-center.center(v-if="!disabled", :title="$tCaption('Add', '@actions')", @click.stop="e => { highlightedStation = null; $emit('add', [station]); }")
							.text {{$tCaption('Add')}}
							.icon.flex-col.align-center.center
								fas(icon="plus")
		.flex-col.space
			.font-big.upper {{$tCaption('Selected stations')}}
			.flex-row.space.wrap
				.station.search-result.flex-col.space.align-center.center.clickable(v-for="station in selected", :key="station.Oid"
					@click="highlightedStation = station", :class="{ highlighted: highlightedStation === station }", :style="{ '--bg': station.HasImage ? `url('${getStationImageUrl(station.Oid)}')` : '#fff' }")
					.flex-col.space.align-center.center.station-info.flex-1
						img(v-if="station.HasImage", :src="getStationImageUrl(station.Oid)")
						.font-big {{`${station.Name}${station.Country ? ` (${station.Country.Code})` : ''}`}}
						.format-names.flex-row.text-center
							.flex-col
								span(v-if="station.FormatNames", v-for="format in getFormatNames(station)") {{format}}
						.slogan(v-if="station.Slogan") {{station.Slogan}}
					.add-button.flex-row.align-center.center(v-if="!disabled", :title="$tCaption('Remove', '@actions')", @click.stop="e => { $emit('remove', station); }")
						.text {{$tCaption('Remove', '@actions')}}
						.icon.flex-col.align-center.center
							fas(icon="minus")
		slot
</template>

<script>
	import _ from 'lodash';
	import CartoMapComponent from 'view/carto/CartoMapComponent.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import circleToPolygon from 'circle-to-polygon';
	import wkt from 'wicket';
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';

	export default {
		translationCategory: 'station.MapSelector',
		name: 'station-map-selector',
		components: {
			CartoMapComponent, Dropdown, Loader, DxButton
		},
		props: ['selected', 'disabled', 'onlyMyStations', 'mapPosition'],
		data() {
			return {
				isLoading: false,
				layers: [{
					query: null,
					customStyle: `
						#layer[zoom>3] {
							polygon-fill: #f27649;
							polygon-opacity: 0.025;
						}
						#layer::outline[zoom>8] {
							line-width: 1;
							line-color: #f27649;
							line-opacity: 0.2;
						}
						#layer::labels[zoom>8] {
							text-name: [station];
							text-face-name: 'DejaVu Sans Book';
							text-size: 10;
							text-fill: #FFFFFF;
							text-label-position-tolerance: 0;
							text-halo-radius: 2;
							text-halo-fill: #f27649;
							text-dy: -10;
							text-allow-overlap: false;
							text-placement: point;
							text-placement-type: dummy;
						}
					`,
					click: null
				}],
				stationName: '',
				searchStations: [],
				isPolygonSearching: false,
				polygonSearchStations: [],
				isPolygonSearchInitialized: false,
				highlightedStation: null,
				dropdownColumns: [{ dataField: 'Name', caption: this.$tCaption('Station'), cellTemplate: 'nameTemplate' }]
			};
		},
		computed: {
			stations() {
				return [
					...this.selected || [],
					...this.polygonSearchStations
				];
			},
			circleDrawings() {
				return [
					...(this.selected || []).map(f => this.getCircleDrawingFromStation(f, {
						fillOpacity: 0.1,
						fill: '#f27649',
						color: '#f27649'
					})),
					...this.filteredPolygonSearchStations.map(f => this.getCircleDrawingFromStation(f, {
						fillOpacity: 0.05,
						fill: '#777',
						color: '#777'
					})),
					...(this.highlightedStation ? [this.getCircleDrawingFromStation(this.highlightedStation, {
						fillOpacity: 0.2,
						fill: '#febe7c',
						color: '#febe7c',
						weight: 4
					})] : [])
				];
			},
			filteredPolygonSearchStations() {
				return this.polygonSearchStations.filter(f => !this.selected.some(s => s.Oid === f.Oid));
			}
		},
		watch: {
		},
		mounted() {
			if (__DEBUG__)
				window.stationMapSelector = this;
		},
		destroyed()	{
			delete this.$selectedStations;
		},
		methods: {
			getFormatNames(station) {
				return (station.FormatNames || '').split(';');
			},
			getStationImageUrl(stationId) {
				return this.$Data.getMethodUrl(`externalApi?accessToken=${this.$auth.accessToken()}&method=Station/GetStationImage&parameters=${encodeURIComponent(`stationId=${stationId}`)}`);
			},
			clearFilters() {
				this.highlightedStation = null;
				if (this.$refs.map)
					this.$refs.map.clearFilters();
			},
			getCircleDrawingFromStation(f, opts) {
				return {
					lat: f.Latitude,
					lng: f.Longtitde,
					radius: f.Radius * 1000,
					fillOpacity: 0.05,
					fill: '#777',
					color: '#777',
					...(opts || {})
				}
			},
			onFilterChanged(type, data) {
				this.searchStationsByRadius([data.lng, data.lat], data.radius);
			},
			onSelectData(v) {
				if (__DEBUG__)
					console.debug('OnSelectData', { v });
				this.$selectedStations = v;
			},
			onStationsSearchOpenedChange(v) {
				if (__DEBUG__)
					console.debug('onStationsSearchOpenedChange', { v: this.$selectedStations });
				if (this.$selectedStations?.length)
					this.$emit('add', this.$selectedStations);
				if (!v) {
					this.searchStations = [];
					this.$selectedStations = [];
				}
			},
			searchStationsByRadius(lngLat, radius) {
				let geoJsonPolygon = circleToPolygon(lngLat, radius, { numberOfEdges: 32 });
				let wktObject = new wkt.Wkt();
				wktObject.fromJson(geoJsonPolygon);

				if (__DEBUG__)
					console.debug('OnFiltersChanged', { geoJsonPolygon, lngLat, radius, wkt: wktObject.toString() });

				this.isPolygonSearchInitialized = !!radius;

				if (this.isPolygonSearchInitialized) {
					this.isPolygonSearching = true;
					this.polygonSearchStations = [];
					this.highlightedStation = null;
					this.$Data.getExternalStations(0, 100, { allCountries: false, allStations: false, polygonWkt: wktObject.toString(), onlyMyStations: this.onlyMyStations }).then(r => {
						this.polygonSearchStations = _.orderBy(r?.Results || [], 'Name');
					}).finally(() => this.isPolygonSearching = false);
				}
			}
		}
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.station-map-selector {
		.stations-map {
			border-radius: 4px;
			overflow: hidden;
			height: 33vh;
			min-height: 340px;

			&.empty {
				height: calc(90vh - 150px);
			}
		}

		.station {
			border-radius: 4px;
			margin: 2px;
			background: #fff;
			flex-basis: 200px;
			min-height: 80px;
			padding: 20px;
			transition: 0.3s;

			.format-names {
				font-weight: 600;
			}

			.slogan {
				font-size: 12px;
				color: #777;
			}

			img {
				max-height: 80px;
			}

			&:hover {
				background: #fff;
				box-shadow: 0px 0px 9px 1px #ddd;
				transform: scale(1.05);
				z-index: 1;
			}

			.station-info {
				
			}

			&:not(.search-result) {
				border: 2px solid $base-accent;
			}

			&.highlighted {
				background: #fff;
				box-shadow: 0px 0px 9px 1px #ddd;
				transform: scale(1.05);
			}

			.add-button {
				background: #e3e3e3;
				border-radius: 4px;

				.text {
					padding: 8px;
					border-radius: 4px 0px 0px 4px;
				}
				
				.icon {
					padding: 6px;
					background: #fff;
					height: 100%;
					border-radius: 0px 4px 4px 0px;
					border: 1px solid #e3e3e3;
				}

				&:hover {
					.text {
						background: $base-accent;
						color: $base-bg;
					}
					.icon {
						color: $base-accent;
					}
				}
			}
		}

		.station-image {
			background: var(--url);
			width: 100px;
			height: 40px;
			background-size: cover;
		}

		.dropdown-image {
			max-height: 40px;
			max-width: 80px;
			border-radius: 4px;
		}
	}
	
</style>
